import { CheckIcon } from "@chakra-ui/icons";
import {
  HStack,
  Text,
  Spinner,
  Button,
  ButtonProps,
  Tooltip,
  Box,
} from "@chakra-ui/react";
import React from "react";
import { Except } from "type-fest";
import { ImageWithMissingSrc } from "../../atoms/ImageWithMissingSrc";
import {
  AnySelectOption,
  StandardSelectOption,
  StandardSelectOptionData,
} from "./SelectOption";

export type OptionRowProps<TOption extends AnySelectOption> = Except<
  ButtonProps,
  "onSelect"
> & {
  option: TOption;
  isSelected: boolean;
  wrapText: boolean;
  onSelect: (value: TOption["value"]) => void;
  OptionContent: OptionContentComponent<TOption>;
};

export type OptionContentProps<TOption extends AnySelectOption> = {
  option: TOption;
  isSelected: boolean;
  wrapText: boolean;
};

export type OptionContentComponent<TOption extends AnySelectOption> = (
  props: OptionContentProps<TOption>,
) => React.ReactElement;

function OptionRowBase<TOption extends AnySelectOption>({
  option,
  isSelected,
  wrapText,
  onSelect,
  OptionContent,
  ...rest
}: OptionRowProps<TOption>): React.ReactElement {
  const row = (
    <Button
      role="option"
      data-group
      minW={0}
      w="full"
      textAlign="left"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      variant="selectorOptionItem"
      gap={3}
      overflow="hidden"
      onClick={() => onSelect(option.value)}
      aria-selected={isSelected}
      {...rest}
    >
      <OptionContent
        option={option}
        isSelected={isSelected}
        wrapText={wrapText}
      />
    </Button>
  );

  if (option.tooltip) {
    return (
      <Tooltip label={option.tooltip} openDelay={1000}>
        {row}
      </Tooltip>
    );
  }

  return row;
}

// This component appears in a long list, so we `memo` it for performance.
// React loses the generic type when using memo, so you must do an assertion
// see: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087
export const OptionRow = React.memo(OptionRowBase) as typeof OptionRowBase;

export function DefaultOptionContent({
  option,
  isSelected,
  wrapText,
}: OptionContentProps<StandardSelectOption>): React.ReactElement {
  const truncateProps =
    wrapText ?
      {
        whiteSpace: "normal" as const,
      }
    : {
        overflow: "hidden" as const,
        textOverflow: "ellipsis" as const,
        whiteSpace: "nowrap" as const,
      };

  return (
    <>
      <OptionIcon data={option.data} />

      {/* Render simple Box with margin on Text for spacing, for performance */}
      <Box minW={0} alignItems="flex-start" flexGrow={1}>
        <Text
          width="full"
          fontWeight={isSelected ? "medium" : undefined}
          fontSize="sm"
          color={isSelected ? "gray.800" : "gray.700"}
          {...truncateProps}
        >
          {option.data.label}
        </Text>
        {option.data.description && (
          <Text
            width="full"
            fontSize="xs"
            color="gray.600"
            {...truncateProps}
            mt={1}
          >
            {option.data.description}
          </Text>
        )}
      </Box>
      {option.data.tag && (
        // use Text instead of Tag for performance
        <Text
          px={2}
          py={0.5}
          borderRadius="md"
          fontSize="sm"
          fontWeight="medium"
          backgroundColor="gray.100"
          color="gray.800"
        >
          {option.data.tag}
        </Text>
      )}
      {isSelected && <CheckIcon w={3.5} color="primary.600" flexShrink={0} />}
    </>
  );
}

function OptionIcon({ data }: { data: StandardSelectOptionData }) {
  if (!("icon" in data)) {
    return null;
  }

  // we still call this even when the icon field is present but undefined
  if (!data.icon || typeof data.icon === "string") {
    return (
      <ImageWithMissingSrc src={data.icon} alt="" boxSize={8} flexShrink={0} />
    );
  }

  return data.icon;
}

/**
 * Loading state if the options are being loaded in.
 */
export function LoadingOptionItem(): React.ReactElement {
  return (
    <HStack
      w="full"
      px={4}
      py={2}
      alignItems="center"
      justifyContent="flex-start"
      gridColumn="1 / -1" // expand across all columns
    >
      <Spinner size="sm" />
      <Text fontSize="sm" color="gray.600">
        Loading...
      </Text>
    </HStack>
  );
}

/**
 * No options at all state.
 */
export function EmptyOptionItem(): React.ReactElement {
  return (
    <HStack
      w="full"
      px={4}
      py={2}
      alignItems="center"
      justifyContent="flex-start"
      gridColumn="1 / -1" // expand across all columns
    >
      <Text fontSize="sm" color="gray.600">
        There are no items to select from.
      </Text>
    </HStack>
  );
}

/**
 * No items matching the search term
 */
export function NoSearchOptionItem(): React.ReactElement {
  return (
    <HStack
      w="full"
      px={4}
      py={2}
      alignItems="center"
      justifyContent="flex-start"
      gridColumn="1 / -1" // expand across all columns
    >
      <Text fontSize="sm" color="gray.600">
        There are no items matching the search term.
      </Text>
    </HStack>
  );
}
